import { useEffect, useCallback, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useSidebar } from "@/context/SidebarContext";
import { useAuth } from "@/hooks/useAuthHooks";
import { useTourStore } from "@/stores/tourStore";
import { getToursByContext, getTourById } from "@/tours/registry";
import type { TourContext, TourDefinition } from "@/types/tour";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { currentPlatform, ePath, fPath, tPath } from "@/utils/paths";

function matchesPath(currentPath: string, tourPath: string, isEntrepreneur: boolean): boolean {
  let resolved: string;
  if (isEntrepreneur) {
    resolved = ePath(tourPath);
  } else if (tourPath.startsWith("/foundershub")) {
    resolved = fPath(tourPath.slice("/foundershub".length) || "/");
  } else {
    resolved = tPath(tourPath);
  }
  if (tourPath.endsWith("/")) return currentPath.startsWith(resolved);
  return currentPath === resolved;
}

function detectTourContext(currentPath: string): TourContext | null {
  const entrepreneurPaths = ["/dashboard", "/matched-opportunities", "/applications", "/matched-opportunity/"];
  if (entrepreneurPaths.some((p) => matchesPath(currentPath, p, true)))
    return "entrepreneur";
  if (currentPlatform() === "foundershub") return "foundershub-admin";
  const adminPaths = [
    "/market-insights",
    "/opportunities",
    "/committees",
    "/post-committee",
    "/project-leads",
    "/projects",
    "/startups",
    "/users",
    "/admin-tools",
  ];
  if (adminPaths.some((p) => currentPath.startsWith(tPath(p))))
    return "terminus-admin";
  return null;
}

function createTourDriver(
  definition: TourDefinition,
  onComplete: () => void,
  onCleanup: () => void,
) {
  const validSteps = definition.steps.filter((step) => {
    if (!step.element) return true;
    return document.querySelector(step.element as string) !== null;
  });

  if (validSteps.length === 0) return null;

  // Track every element we've mutated so destroy cleans up all of them, not
  // just the last-highlighted one. A stale reference after React detaches a
  // node is fine — clearing inline styles on an orphan is a no-op.
  const mutatedElements = new Set<HTMLElement>();
  // Remember the element's original inline position so we can restore it;
  // only override when the computed position was "static", because any other
  // value (fixed, absolute, sticky) is load-bearing for the element's layout.
  const originalPositions = new WeakMap<HTMLElement, string>();

  const applyHighlightEffect = (el: Element) => {
    const htmlEl = el as HTMLElement;
    const computed = window.getComputedStyle(htmlEl).position;
    if (computed === "static") {
      originalPositions.set(htmlEl, htmlEl.style.position);
      htmlEl.style.position = "relative";
    }
    htmlEl.style.zIndex = "1000000001";
    htmlEl.classList.add("driver-vess-glow");
    mutatedElements.add(htmlEl);
  };

  const removeHighlightEffect = (el: Element) => {
    const htmlEl = el as HTMLElement;
    htmlEl.style.zIndex = "";
    if (originalPositions.has(htmlEl)) {
      htmlEl.style.position = originalPositions.get(htmlEl) ?? "";
      originalPositions.delete(htmlEl);
    }
    htmlEl.classList.remove("driver-vess-glow");
    mutatedElements.delete(htmlEl);
  };

  let previousElement: Element | null = null;

  const driverInstance = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayColor: "rgba(0, 0, 0, 0.6)",
    stagePadding: 8,
    stageRadius: 8,
    ...definition.driverConfig,
    steps: validSteps,
    onHighlighted: (el) => {
      if (previousElement) removeHighlightEffect(previousElement);
      if (el) {
        applyHighlightEffect(el);
        previousElement = el;
      }
    },
    onDestroyStarted: () => {
      // Clean up every element ever highlighted, not just the most recent.
      mutatedElements.forEach((el) => removeHighlightEffect(el));
      mutatedElements.clear();
      previousElement = null;
      driverInstance.destroy();
    },
    onDestroyed: () => {
      onComplete();
      onCleanup();
    },
  });

  return driverInstance;
}

export function useTriggerTour(tourId: string) {
  const firedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  return useCallback(() => {
    if (firedRef.current) return;
    const store = useTourStore.getState();
    if (store.isTourCompleted(tourId) || store.activeTourId) return;
    const definition = getTourById(tourId);
    if (!definition) return;
    firedRef.current = true;
    store.setActiveTour(definition.id);
    const instance = createTourDriver(
      definition,
      () => store.markCompleted(definition.id),
      () => {
        driverRef.current = null;
        useTourStore.getState().setActiveTour(null);
      },
    );
    if (instance) {
      driverRef.current = instance;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        instance.drive();
      }, 800);
    } else {
      store.setActiveTour(null);
    }
  }, [tourId]);
}

export function useTour() {
  const { currentPath } = useSidebar();
  const { isAuthenticated } = useAuth();
  const toursEnabled = useBooleanFlagValue("tours_enabled", true);
  const syncCompletions = useTourStore((s) => s.syncCompletions);
  const isSynced = useTourStore((s) => s.isSynced);
  const isTourCompleted = useTourStore((s) => s.isTourCompleted);
  const markCompleted = useTourStore((s) => s.markCompleted);
  const activeTourId = useTourStore((s) => s.activeTourId);
  const setActiveTour = useTourStore((s) => s.setActiveTour);
  const autoShowFired = useRef<Set<string>>(new Set());
  const activeDriverRef = useRef<ReturnType<typeof driver> | null>(null);

  const tourContext = detectTourContext(currentPath);

  useEffect(() => {
    if (isAuthenticated && !isSynced) {
      syncCompletions().catch(() => {});
    }
  }, [isAuthenticated, isSynced, syncCompletions]);

  useEffect(() => {
    return () => {
      if (activeDriverRef.current) {
        activeDriverRef.current.destroy();
        activeDriverRef.current = null;
      }
    };
  }, []);

  const startTour = useCallback(
    (definition: TourDefinition, markAsCompleted: boolean = true) => {
      setActiveTour(definition.id);
      const instance = createTourDriver(
        definition,
        () => { if (markAsCompleted) markCompleted(definition.id); },
        () => {
          activeDriverRef.current = null;
          setActiveTour(null);
        },
      );
      if (instance) {
        activeDriverRef.current = instance;
        instance.drive();
      } else {
        setActiveTour(null);
      }
    },
    [markCompleted, setActiveTour]
  );

  useEffect(() => {
    if (!toursEnabled || !isAuthenticated || !isSynced || !tourContext)
      return;
    if (activeTourId) return;

    const tours = getToursByContext(tourContext);
    const isEntrepreneur = tourContext === "entrepreneur";
    const pendingTour = tours.find(
      (t) => !t.manual && !isTourCompleted(t.id) && (!t.path || matchesPath(currentPath, t.path, isEntrepreneur))
    );
    if (!pendingTour) return;

    if (autoShowFired.current.has(pendingTour.id)) return;
    autoShowFired.current.add(pendingTour.id);

    const timer = setTimeout(() => {
      if (!useTourStore.getState().activeTourId) {
        startTour(pendingTour);
      }
    }, 800);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    toursEnabled,
    isAuthenticated,
    isSynced,
    tourContext,
    activeTourId,
    isTourCompleted,
    startTour,
  ]);

}
